import React, { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Modal, Button, Form } from "react-bootstrap";

// Stable unique id so list items (and deletes) don't depend on array position.
const createContactId = () => {
  const bytes = new Uint32Array(2);
  window.crypto.getRandomValues(bytes);
  return `${Date.now()}-${bytes[0].toString(36)}${bytes[1].toString(36)}`;
};

// Contacts saved before ids existed get one when they are loaded.
const withId = (contact) =>
  contact.id ? contact : { ...contact, id: createContactId() };

const ContactsPage = () => {
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [newContact, setNewContact] = useState({
    name: "",
    email: "",
    phone: "",
    country: "India (+91)",
  });

  // ✅ Load contacts from localStorage when page loads
  useEffect(() => {
    const stored = localStorage.getItem("contacts");
    if (stored) setContacts(JSON.parse(stored).map(withId));
  }, []);

  // ✅ Save contacts to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("contacts", JSON.stringify(contacts));
  }, [contacts]);

  const handleAddContact = () => {
    if (!newContact.name || !newContact.email) return;

    const updated = [
      ...contacts,
      {
        id: createContactId(),
        name: newContact.name,
        email: newContact.email,
        phone: newContact.phone,
        country: newContact.country,
      },
    ];
    setContacts(updated);

    setNewContact({ name: "", email: "", phone: "", country: "India (+91)" });
    setShowModal(false);
  };

  // ✅ Delete contact
  const handleDeleteContact = (id) => {
    const updated = contacts.filter((c) => c.id !== id);
    setContacts(updated);
  };

  // ✅ Filtering based on Name or Email
  const filteredContacts = contacts.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-black">Contacts</h2>
        <button
          className="bg-green-600 text-white p-2 rounded-full hover:bg-green-700"
          onClick={() => setShowModal(true)}
        >
          <Plus size={20} />
        </button>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search Contacts"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="border rounded px-3 py-2 w-full text-black placeholder-gray-500"
      />

      {/* Contacts List */}
      <div className="space-y-2 mt-3">
        {filteredContacts.length > 0 ? (
          filteredContacts.map((contact) => (
            <div
              key={contact.id}
              className="flex items-center justify-between p-2 rounded bg-green-50"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-purple-400 text-black font-bold">
                  {contact.name[0]}
                </div>

                <div>
                  <p className="font-semibold text-black">{contact.name}</p>
                  <p className="text-sm text-gray-600">{contact.email}</p>
                </div>
              </div>

              {/* Delete button */}
              <button
                className="text-red-500 hover:text-red-700"
                onClick={() => handleDeleteContact(contact.id)}
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))
        ) : (
          <p className="text-gray-500 text-center">No contacts found</p>
        )}
      </div>

      {/* Modal for Add Contact */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Contact</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={newContact.name}
                onChange={(e) =>
                  setNewContact({ ...newContact, name: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={newContact.email}
                onChange={(e) =>
                  setNewContact({ ...newContact, email: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone number</Form.Label>
              <Form.Control
                type="text"
                value={newContact.phone}
                onChange={(e) =>
                  setNewContact({ ...newContact, phone: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Country code</Form.Label>
              <Form.Select
                value={newContact.country}
                onChange={(e) =>
                  setNewContact({ ...newContact, country: e.target.value })
                }
              >
                <option>India (+91)</option>
                <option>USA (+1)</option>
                <option>UK (+44)</option>
                <option>Australia (+61)</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleAddContact}>
            Add
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ContactsPage;